import { createSilentLogger } from '@ibe/logger';
import { isConsentGranted } from './consent.js';
import { getProviderDefinition, isAllowedProvider } from './provider-allowlist.js';
import { Ga4Adapter } from './adapters/ga4-adapter.js';
import { GtmAdapter } from './adapters/gtm-adapter.js';
import { TripteaseAdapter } from './adapters/triptease-adapter.js';
import { ProviderScriptAdapter } from './adapters/provider-script-adapter.js';
import { PixelAdapter } from './adapters/pixel-adapter.js';
const ADAPTER_REGISTRY = {
    GTM: () => new GtmAdapter(),
    GA4: () => new Ga4Adapter(),
    TRIPTEASE: () => new TripteaseAdapter(),
    PROVIDER_SCRIPT: () => new ProviderScriptAdapter(),
    PIXEL: () => new PixelAdapter(),
};
const MAX_REPLAY_QUEUE = 20;
export class AnalyticsService {
    config;
    consent;
    logger;
    adapters = new Map();
    context;
    queuedEvents = [];
    isFlushing = false;
    constructor(options) {
        this.config = options.config;
        this.consent = options.consent;
        this.logger = options.logger ?? createSilentLogger();
        this.context = {
            tenantId: this.config.tenantId,
            propertyId: this.config.propertyId,
            consent: this.consent,
            logger: this.logger,
        };
        for (const integration of this.config.integrations) {
            this.loadAdapter(integration);
        }
    }
    track(event) {
        // While the user has not recorded a choice, queue a bounded replay window.
        if (this.consent === null && !this.isFlushing && this.queuedEvents.length < MAX_REPLAY_QUEUE) {
            this.queuedEvents.push(event);
            return;
        }
        for (const integration of this.config.integrations) {
            const definition = getProviderDefinition(integration.provider);
            if (definition !== undefined) {
                if (!isConsentGranted(this.consent, definition.consentCategory))
                    continue;
            }
            const adapter = this.adapters.get(integration.provider);
            if (adapter === undefined)
                continue;
            try {
                const trackResult = adapter.track(event, integration, this.context);
                if (trackResult instanceof Promise) {
                    // Never await analytics; run it in a best-effort microtask.
                    void trackResult.catch((error) => {
                        this.logger.warn('AnalyticsService: track failed', {
                            provider: integration.provider,
                            event: event.kind,
                            error: error instanceof Error ? error.message : 'unknown',
                        });
                    });
                }
            }
            catch (error) {
                this.logger.warn('AnalyticsService: track threw synchronously', {
                    provider: integration.provider,
                    event: event.kind,
                    error: error instanceof Error ? error.message : 'unknown',
                });
            }
        }
    }
    setConsent(state) {
        this.consent = state;
        this.context = { ...this.context, consent: this.consent };
        // Load any adapters that are now permitted.
        for (const integration of this.config.integrations) {
            const definition = getProviderDefinition(integration.provider);
            if (definition !== undefined && !isConsentGranted(this.consent, definition.consentCategory)) {
                continue;
            }
            this.loadAdapter(integration);
        }
        // Flush the bounded replay queue once.
        if (this.queuedEvents.length === 0 || this.isFlushing)
            return;
        this.isFlushing = true;
        const toFlush = this.queuedEvents.splice(0);
        for (const event of toFlush) {
            this.track(event);
        }
        this.isFlushing = false;
    }
    loadAdapter(integration) {
        if (!isAllowedProvider(integration.provider)) {
            this.logger.warn('AnalyticsService: unknown provider', { provider: integration.provider });
            return;
        }
        const definition = getProviderDefinition(integration.provider);
        if (definition !== undefined && !isConsentGranted(this.consent, definition.consentCategory)) {
            return;
        }
        const adapter = this.getAdapter(integration.provider);
        if (adapter === undefined)
            return;
        try {
            void adapter.load(integration, this.context);
        }
        catch (error) {
            this.logger.warn('AnalyticsService: adapter load failed', {
                integrationId: integration.id,
                error: error instanceof Error ? error.message : 'unknown',
            });
        }
    }
    getAdapter(provider) {
        let adapter = this.adapters.get(provider);
        if (adapter !== undefined)
            return adapter;
        const factory = ADAPTER_REGISTRY[provider];
        if (factory === undefined)
            return undefined;
        adapter = factory();
        this.adapters.set(provider, adapter);
        return adapter;
    }
}
//# sourceMappingURL=analytics-service.js.map